import { apiClient, unwrap } from './client';

export interface HoldingFilialeKpi {
  id:                  string;
  filialeId:           string;
  periode:             string;
  currency:            string;
  caCents:             string;
  chargesCents:        string;
  resultatBrutCents:   string;
  ebitdaCents:         string;
  tresorerieCents:     string;
  bfrCents:            string;
  margeBruteCents:     string;
  caFcfaCents:         string;
  chargesFcfaCents:    string;
  ebitdaFcfaCents:     string;
  tresorerieFcfaCents: string;
  employeesCount:      number;
  dsoDays:             number;
  metrics:             Record<string, number>;
  computedAt?:         string;
  /** false = filiale sans base dédiée, ses chiffres ne remontent pas encore. */
  raccordee?:          boolean;
  /** Renseigné quand la filiale figure à zéro faute de chiffres. */
  motif?:              'FILIALE_NON_RACCORDEE' | 'AUCUN_SNAPSHOT_PERIODE' | null;
  filiale: {
    filialeKey:   string;
    name:         string;
    pays:         string;
    devise:       string;
    logoPath:     string;
    colorPrimary: string;
  };
}

export interface HoldingKpis {
  periode:  string;
  filiales: HoldingFilialeKpi[];
  /**
   * A-3 — Le total groupe est calculé par le serveur, jamais par l'écran.
   * Un total recalculé côté client est un deuxième calcul : c'est ce qui a
   * produit trois encours différents en finance.
   */
  groupe: {
    caFcfaCents:               string;
    chargesFcfaCents:          string;
    ebitdaFcfaCents:           string;
    tresorerieFcfaCents:       string;
    employeesCount:            number;
    nombreFiliales:            number;
    nombreFilialesTotal:       number;
    nombreFilialesSansDonnees: number;
  };
}

export interface HoldingRecomputeResult {
  recomputedAt: string;
  periode:      string;
}

// ─── Ticket SIMGROUP-2026-003 Sprint 1 — Mentions légales OHADA ─────────
export interface LegalSiegeSocial {
  adresseLibre?:   string;
  ville?:          string;
  arrondissement?: string;
  lot?:            string;
  ilot?:           string;
  section?:        string;
  parcelle?:       string;
}

export interface FilialeLegal {
  id:                     string;
  filialeKey:             string;
  name:                   string;
  fullName:               string;
  pays:                   string;
  devise:                 string;
  formeJuridique:         string | null;
  capitalSocialCents:     string | null;
  rccm:                   string | null;
  numeroContribuable:     string | null;
  regimeTva:              string | null;
  siegeSocial:            LegalSiegeSocial | null;
  telephone:              string | null;
  emailContact:           string | null;
  dirigeantNom:           string | null;
  dirigeantTitre:         string | null;
  dirigeantSignatureUrl:  string | null;
  /** Champs obligatoires absents — bloqueront l'émission PDF (Sprint 2) */
  missingLegal:           string[];
}

export type UpdateLegalMentionsPayload = Partial<Omit<FilialeLegal,
  'id' | 'filialeKey' | 'name' | 'fullName' | 'pays' | 'devise' | 'missingLegal'>>;

export const holdingApi = {
  getKpis: async (periode?: string): Promise<HoldingKpis> => {
    const res = await apiClient.get('/holding/kpis', { params: periode ? { periode } : undefined });
    return unwrap<HoldingKpis>(res);
  },

  /**
   * Force le recalcul des KPI sur les tenants actifs (admin_holding uniquement).
   * Utilisé par le bouton "Recalculer maintenant" du dashboard Holding.
   */
  recomputeKpis: async (periode?: string): Promise<HoldingRecomputeResult> => {
    const res = await apiClient.post('/holding/kpis/recompute', null, {
      params: periode ? { periode } : undefined,
    });
    return res.data as HoldingRecomputeResult;
  },

  /** Récupère la fiche filiale complète + mentions légales OHADA */
  getFilialeLegal: async (filialeKey: string): Promise<FilialeLegal> => {
    const res = await apiClient.get(`/holding/filiales/${filialeKey}/legal`);
    return unwrap<FilialeLegal>(res);
  },

  /** Met à jour les mentions légales (admin_holding uniquement) */
  updateFilialeLegal: async (filialeKey: string, payload: UpdateLegalMentionsPayload): Promise<FilialeLegal> => {
    const res = await apiClient.patch(`/holding/filiales/${filialeKey}/legal`, payload);
    return unwrap<FilialeLegal>(res);
  },
};
